import { useCallback, useEffect, useState } from 'react';
import { cccdAdmin, type CatalogOption } from '@/lib/api/cccdAdminClient';
import ModuleLayout from '@/components/admin/ModuleLayout';
import Alert from '@/components/ui/alert/Alert';
import { toastError, toastSuccess } from '@/lib/toast';
import { Toggle, inputCls } from '@/components/admin/FormComponents';
import { SectionHeader } from '@/components/cccd/SectionHeader';
import { CheckLineIcon, PlusIcon } from '@/icons';
import { LoadState } from "@/components/ui/LoadState"

const cellInputCls =
  'h-[38px] w-full rounded-lg border border-gray-300 px-3 text-[13px] text-gray-800 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white';

const PAGE_SIZE = 10;

// fieldKey conocidos hoy (ver openspec/changes/cccd-institution-tracking) —
// la lista es solo para el selector de esta pantalla; el backend acepta
// cualquier fieldKey, así que agregar un catálogo nuevo no requiere cambios
// de código, solo elegir "Otro campo" y escribir la clave.
const KNOWN_FIELD_KEYS: { value: string; label: string }[] = [
  { value: 'motor', label: 'Motor' },
  { value: 'forms', label: 'Forms' },
  { value: 'fotos_rrss', label: 'Fotos RRSS' },
  { value: 'kit_status', label: 'Kit (entrega)' },
  { value: 'altimetro_status', label: 'Altímetro (entrega)' },
  { value: 'inscripcion_status', label: 'Inscripción' },
  { value: 'estado_operativo', label: 'Estado operativo' },
];

type EditableField = 'label' | 'sortOrder';
type OptionEdits = Partial<Record<EditableField, string | number>>;

// Mismo diseño de catálogo global inline-editable que AddonsCatalogPage
// (cccd-admin-design-system): fila con guardar-por-fila, sin "guardar todo".
// A diferencia de complementos, aquí no hay soft-delete — el backend solo
// expone toggle de isActive y edición de label/sortOrder, `value` es
// inmutable tras crearse (es la clave interna guardada en CccdTeam).
export default function CatalogOptionsPage() {
  const [fieldKey, setFieldKey] = useState(KNOWN_FIELD_KEYS[0].value);
  const [customFieldKey, setCustomFieldKey] = useState('');
  const [options, setOptions] = useState<CatalogOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [edits, setEdits] = useState<Record<string, OptionEdits>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const [newValue, setNewValue] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [creating, setCreating] = useState(false);

  const activeFieldKey = fieldKey === '__custom__' ? customFieldKey.trim() : fieldKey;

  const load = useCallback(async () => {
    if (!activeFieldKey) { setOptions([]); return; }
    setLoading(true);
    setError(null);
    try {
      setOptions(await cccdAdmin.listCatalogOptions(activeFieldKey, true));
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Error al cargar el catálogo.');
    } finally {
      setLoading(false);
    }
  }, [activeFieldKey]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(1); setEdits({}); }, [activeFieldKey]);

  const totalPages = Math.max(1, Math.ceil(options.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageOptions = options.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  function displayValue(option: CatalogOption, field: EditableField): string | number {
    return edits[option.id]?.[field] ?? option[field];
  }

  function stageEdit(option: CatalogOption, field: EditableField, raw: string) {
    const value: string | number = field === 'sortOrder' ? (Number(raw) || 0) : raw;
    setEdits((prev) => {
      const current = { ...(prev[option.id] ?? {}) };
      if (value === option[field]) {
        delete current[field];
      } else {
        current[field] = value;
      }
      const next = { ...prev };
      if (Object.keys(current).length === 0) delete next[option.id];
      else next[option.id] = current;
      return next;
    });
  }

  async function saveRow(option: CatalogOption) {
    const patch = edits[option.id];
    if (!patch) return;
    setSavingId(option.id);
    try {
      await cccdAdmin.updateCatalogOption(option.id, patch as never);
      setEdits((prev) => {
        const next = { ...prev };
        delete next[option.id];
        return next;
      });
      toastSuccess(`"${option.label}" actualizado.`);
      await load();
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? `No se pudo guardar "${option.label}".`);
    } finally {
      setSavingId(null);
    }
  }

  async function onCreate() {
    if (!activeFieldKey || !newValue.trim() || !newLabel.trim()) return;
    setCreating(true);
    setError(null);
    try {
      await cccdAdmin.createCatalogOption({
        fieldKey: activeFieldKey,
        value: newValue.trim(),
        label: newLabel.trim(),
        sortOrder: options.length,
      });
      setNewValue('');
      setNewLabel('');
      await load();
      setPage(Math.ceil((options.length + 1) / PAGE_SIZE));
      toastSuccess(`"${newLabel.trim()}" agregado al catálogo.`);
    } catch (err: any) {
      const message = err?.response?.data?.error ?? 'Error al crear la opción (¿ya existe ese valor para este campo?).';
      setError(message);
      toastError(message);
    } finally {
      setCreating(false);
    }
  }

  async function toggleActive(option: CatalogOption) {
    setOptions((prev) => prev.map((o) => (o.id === option.id ? { ...o, isActive: !o.isActive } : o)));
    try {
      await cccdAdmin.updateCatalogOption(option.id, { isActive: !option.isActive });
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'Error al actualizar la opción.');
      await load();
    }
  }

  return (
    <ModuleLayout
      breadcrumbs={[{ label: 'SateLab', to: '/' }, { label: 'CCCD', to: '/cccd' }, 'Catálogos']}
      title="Catálogos CCCD"
      subtitle="Valores de la ficha de equipo (Motor, Forms, Kit, Inscripción, etc.) — agrega valores nuevos sin necesitar un despliegue"
      tabs={[]}
      metrics={[]}
    >
      <SectionHeader
        title="Campo del catálogo"
        subtitle="Elige qué lista de valores estás editando"
        icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>}
        className="mb-5"
      >
        <div className="flex flex-wrap gap-1.5">
          {KNOWN_FIELD_KEYS.map((k) => (
            <button
              key={k.value}
              type="button"
              onClick={() => setFieldKey(k.value)}
              className={`rounded-lg px-3.5 py-1.5 text-[13px] font-medium transition-colors ${
                fieldKey === k.value
                  ? 'bg-brand-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {k.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setFieldKey('__custom__')}
            className={`flex items-center gap-1 rounded-lg border-[1.5px] border-dashed px-3.5 py-1.5 text-[13px] font-medium transition-colors ${
              fieldKey === '__custom__'
                ? 'border-brand-300 bg-brand-50 text-brand-600 dark:border-brand-700 dark:bg-brand-500/10 dark:text-brand-400'
                : 'border-gray-300 text-gray-400 hover:border-brand-300 hover:text-brand-500 dark:border-gray-700'
            }`}
          >
            <PlusIcon className="size-3.5" />
            Otro campo
          </button>
        </div>
        {fieldKey === '__custom__' && (
          <div className="mt-3.5 max-w-xs">
            <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">field_key (snake_case)</label>
            <input className={inputCls} value={customFieldKey} onChange={(e) => setCustomFieldKey(e.target.value)} placeholder="ej: nuevo_campo" autoFocus />
          </div>
        )}
      </SectionHeader>

      {error && <Alert variant="error" title="Error" message={error} />}

      <div className="mb-5 rounded-2xl border border-dashed border-gray-300 bg-gray-25 p-5 dark:border-gray-700 dark:bg-white/[0.02]">
        <p className="mb-3 text-sm font-semibold text-gray-600 dark:text-gray-400">Agregar opción nueva</p>
        <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-3">
          <div>
            <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Valor interno</label>
            <input
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder="ej: D12-5"
              className={cellInputCls.replace('h-[38px]', 'h-10')}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Etiqueta visible</label>
            <input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="ej: D12-5"
              className={cellInputCls.replace('h-[38px]', 'h-10')}
            />
          </div>
          <button
            type="button"
            onClick={onCreate}
            disabled={!activeFieldKey || !newValue.trim() || !newLabel.trim() || creating}
            className="h-10 whitespace-nowrap rounded-lg bg-brand-500 px-[18px] text-[13px] font-medium text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {creating ? 'Agregando…' : '+ Agregar'}
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] gap-3 border-b border-gray-100 bg-gray-25 px-5 py-3 dark:border-gray-800 dark:bg-white/[0.02]">
          {['Etiqueta', 'Valor interno', 'Orden', 'Activa', ''].map((h, i) => (
            <span key={i} className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{h}</span>
          ))}
        </div>

        {loading ? (
          <LoadState />
        ) : pageOptions.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">Sin opciones para este campo todavía.</p>
        ) : (
          pageOptions.map((option) => {
            const isDirty = Boolean(edits[option.id]);
            const isSaving = savingId === option.id;
            return (
              <div key={option.id} className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] items-center gap-3 border-b border-gray-100 px-5 py-3 last:border-b-0 dark:border-gray-800">
                <input
                  type="text"
                  value={displayValue(option, 'label')}
                  onChange={(e) => stageEdit(option, 'label', e.target.value)}
                  className={cellInputCls}
                />
                <code className="w-fit rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400">{option.value}</code>
                <input
                  type="number"
                  value={displayValue(option, 'sortOrder')}
                  onChange={(e) => stageEdit(option, 'sortOrder', e.target.value)}
                  className={cellInputCls}
                />
                <Toggle checked={option.isActive} onChange={() => toggleActive(option)} label="" />
                <button
                  type="button"
                  onClick={() => saveRow(option)}
                  disabled={!isDirty || isSaving}
                  title={isDirty ? 'Guardar cambios' : 'Sin cambios'}
                  className={`flex size-7 items-center justify-center rounded-lg border transition-colors ${
                    isDirty
                      ? 'border-brand-100 bg-brand-50 text-brand-500 hover:bg-brand-100 dark:border-brand-500/20 dark:bg-brand-500/15 dark:text-brand-400'
                      : 'cursor-not-allowed border-gray-100 bg-gray-50 text-gray-300 dark:border-gray-800 dark:bg-gray-800 dark:text-gray-600'
                  }`}
                >
                  <CheckLineIcon className="size-3.5" />
                </button>
              </div>
            );
          })
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <span>Página {currentPage} de {totalPages} · {options.length} opciones</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-40 dark:border-gray-700 dark:hover:bg-gray-800"
            >
              Anterior
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-40 dark:border-gray-700 dark:hover:bg-gray-800"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </ModuleLayout>
  );
}
